import { useEffect, useState } from 'react';
import { api, type AppView } from '../api/client';
import { UnrestrictedCallout } from '../components/AccessBadge';
import { AppForm } from '../components/AppForm';
import { DangerZone } from '../components/DangerZone';
import { ErrorBanner } from '../components/ErrorBanner';
import { LoginUrlBox } from '../components/LoginUrlBox';
import { navigate } from '../router';

export function AppDetail({
  identifier,
  adminClientId,
}: {
  identifier: string;
  adminClientId: string;
}) {
  const [app, setApp] = useState<AppView | undefined>();
  const [error, setError] = useState<unknown>();
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setApp(undefined);
    api.get(identifier).then(setApp).catch(setError);
  }, [identifier]);

  async function run(action: () => Promise<AppView>, then?: () => void) {
    setBusy(true);
    setError(undefined);
    try {
      const next = await action();
      setApp(next);
      then?.();
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  }

  if (error && !app) return <ErrorBanner error={error} />;
  if (!app) return <p className="muted">Loading…</p>;

  const isSelf = app.client_id === adminClientId;

  return (
    <>
      <div className="pagehead">
        <h1>{app.app_name}</h1>
        {isSelf && <span className="tag">this console</span>}
      </div>

      <ErrorBanner error={error} />

      {app.required_group_ids.length === 0 && <UnrestrictedCallout />}

      <dl className="meta">
        <dt>Client ID</dt>
        <dd>
          <code>{app.client_id}</code>
        </dd>
        <dt>Created</dt>
        <dd>{app.created_at}</dd>
        <dt>Last updated</dt>
        <dd>
          {app.updated_at} by {app.updated_by ?? <span className="muted">(not recorded)</span>}
        </dd>
      </dl>

      <LoginUrlBox app={app} />

      <section>
        <h2>Configuration</h2>
        <AppForm
          initial={{
            appName: app.app_name,
            redirectUris: app.redirect_uris,
            requiredGroupIds: app.required_group_ids,
          }}
          nameEditable={false}
          submitLabel="Save changes"
          busy={busy}
          onSubmit={(values) =>
            void run(() =>
              api.update(app.client_id, {
                redirectUris: values.redirectUris,
                requiredGroupIds: values.requiredGroupIds,
              }),
            )
          }
        />
      </section>

      <section>
        <h2>Availability</h2>
        <p className="muted">
          Disabling denies all logins for this app without deleting the client. It takes effect on
          the next token issuance, including refreshes, and is instantly reversible — prefer it to
          deletion.
        </p>
        {app.enabled ? (
          <button
            type="button"
            disabled={busy || isSelf}
            onClick={() => void run(() => api.setEnabled(app.client_id, false))}
          >
            Disable {app.app_name}
          </button>
        ) : (
          <button
            type="button"
            disabled={busy}
            onClick={() => void run(() => api.setEnabled(app.client_id, true))}
          >
            Enable {app.app_name}
          </button>
        )}
        {isSelf && app.enabled && (
          <p className="hint">
            Disabling this console is refused — it would lock everyone out. Use the CLI if you really
            mean to.
          </p>
        )}
      </section>

      <DangerZone
        app={app}
        isSelf={isSelf}
        busy={busy}
        onDelete={(confirm) =>
          void run(
            () => api.remove(app.client_id, confirm),
            () => navigate('/'),
          )
        }
      />
    </>
  );
}
